import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import image_google from "../../../assets/img/images.png";
import "../../../assets/css/Youtube.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const YOUTUBE_URL = process.env.REACT_APP_YOUTUBE_URL;
const GOOGLE_URL = process.env.REACT_APP_GOOGLE_URL;

function Youtube() {
  const { section, id } = useParams();
  const [servicesData, setServicesData] = useState([]);
  const [pageYoutube, setPageYoutube] = useState(1);
  const [youtubePagination, setYoutubePagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 3,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    // Keep the original random-page behaviour for non-home sections.
    if (section && section !== "Home") {
      setPageYoutube(Math.floor(Math.random() * 5) + 1);
    } else {
      setPageYoutube(1);
    }
  }, [section]);

  useEffect(() => {
    let active = true;

    async function fetchYoutubeData() {
      setLoading(true);
      setError(false);

      try {
        await axios.get("sanctum/csrf-cookie");

        const response = await axios.get(
          `${BASE_URL}posdata?columes[eq]=title_advert_utube&ministry[eq]=0&status02[eq]=publish&status[eq]=Public&visibility[eq]=Public&page=${pageYoutube}&per_page=3`,
        );

        if (!active) return;

        if (response?.data?.status === 200) {
          setServicesData(response?.data?.allData || []);
          setYoutubePagination(
            response?.data?.pagination || {
              current_page: pageYoutube,
              last_page: 1,
              per_page: 3,
              total: 0,
            },
          );
        } else {
          setServicesData([]);
        }
      } catch (err) {
        if (active) {
          setError(true);
          console.error("Unable to load FMW media spotlight:", err);
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    fetchYoutubeData();
    return () => {
      active = false;
    };
  }, [section, id, pageYoutube, retryKey]);

  const changePage = (page) => {
    const lastPage = Math.max(1, Number(youtubePagination?.last_page || 1));
    if (page >= 1 && page <= lastPage) {
      setPageYoutube(page);
    }
  };

  if (!loading && !error && servicesData.length === 0) return null;

  return (
    <section id="media-spotlight" className="fmw-media-spotlight">
      <div className="container">
        <header className="fmw-media-heading">
          <div>
            <span className="fmw-media-kicker">
              FILMS · STORIES · PUBLIC INFORMATION
            </span>
            <h2>Media Spotlight</h2>
          </div>
          <p className="fmw-journal-section-label">
            <i className="fa-solid fa-circle-play" aria-hidden="true"></i>
          </p>
        </header>

        {loading && (
          <div className="fmw-media-status" role="status" aria-live="polite">
            <span className="fmw-media-spinner" />
            Loading media…
          </div>
        )}

        {error && servicesData.length === 0 && (
          <div className="fmw-media-message" role="alert">
            <h3>Media is temporarily unavailable.</h3>
            <p>Please check your connection and try again.</p>
            <button
              type="button"
              onClick={() => setRetryKey((value) => value + 1)}
            >
              Try Again
            </button>
          </div>
        )}

        {!error && servicesData.length > 0 && (
          <>
            <div className="fmw-media-grid" aria-busy={loading}>
              {servicesData.map((service, index) => {
                const [videoId = "", provider = ""] = String(
                  service.addressLink || "",
                ).split("*Locat*");
                const isYoutube = provider === "youtube";
                const externalUrl = isYoutube
                  ? `${YOUTUBE_URL}${videoId}`
                  : `${GOOGLE_URL}${videoId}`;

                return (
                  <article className="fmw-media-card" key={service.id || index}>
                    <div className="fmw-media-card-top">
                      <span className="fmw-media-index">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="fmw-media-provider">
                        <i
                          className={`fa-brands ${isYoutube ? "fa-youtube" : "fa-google-drive"}`}
                          aria-hidden="true"
                        />
                        {isYoutube ? "YouTube" : "Video resource"}
                      </span>
                    </div>

                    <div className="fmw-media-frame">
                      {isYoutube && videoId ? (
                        <iframe
                          src={`${YOUTUBE_URL}${videoId}`}
                          title={
                            service.title || `FMW media video ${index + 1}`
                          }
                          loading="lazy"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          referrerPolicy="strict-origin-when-cross-origin"
                        />
                      ) : (
                        <Link
                          to={externalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="fmw-media-external"
                          aria-label={`Open ${service.title || "media resource"} in a new tab`}
                        >
                          <img src={image_google} alt="" loading="lazy" />
                          <span>Open video resource</span>
                          <span
                            className="fmw-media-external-arrow"
                            aria-hidden="true"
                          >
                            ↗
                          </span>
                        </Link>
                      )}
                    </div>

                    {service.title && (
                      <div className="fmw-media-card-caption">
                        <h3>{service.title}</h3>
                      </div>
                    )}

                    <div className="fmw-media-card-footer">
                      <span>FEDERAL MINISTRY OF WORKS</span>
                      <Link
                        to={externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Watch externally <span aria-hidden="true">↗</span>
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>

            <nav className="fmw-media-pagination" aria-label="Media pagination">
              <span>
                PAGE{" "}
                <strong>
                  {youtubePagination?.current_page || pageYoutube}
                </strong>{" "}
                OF {youtubePagination?.last_page || 1}
              </span>
              <div className="fmw-media-page-buttons">
                <button
                  type="button"
                  onClick={() => changePage(1)}
                  disabled={
                    Number(youtubePagination?.current_page || pageYoutube) <=
                      1 || loading
                  }
                  aria-label="First page"
                >
                  «
                </button>
                <button
                  type="button"
                  onClick={() =>
                    changePage(
                      Number(youtubePagination?.current_page || pageYoutube) -
                        1,
                    )
                  }
                  disabled={
                    Number(youtubePagination?.current_page || pageYoutube) <=
                      1 || loading
                  }
                  aria-label="Previous page"
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={() =>
                    changePage(
                      Number(youtubePagination?.current_page || pageYoutube) +
                        1,
                    )
                  }
                  disabled={
                    Number(youtubePagination?.current_page || pageYoutube) >=
                      Number(youtubePagination?.last_page || 1) || loading
                  }
                  aria-label="Next page"
                >
                  ›
                </button>
                <button
                  type="button"
                  onClick={() =>
                    changePage(Number(youtubePagination?.last_page || 1))
                  }
                  disabled={
                    Number(youtubePagination?.current_page || pageYoutube) >=
                      Number(youtubePagination?.last_page || 1) || loading
                  }
                  aria-label="Last page"
                >
                  »
                </button>
              </div>
            </nav>
          </>
        )}
      </div>
    </section>
  );
}

export default React.memo(Youtube);
